import { Alert, Autocomplete, Divider, Grid, TextField, Typography } from '@mui/material';
import { FormikProps } from 'formik';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { EnumerationMultiSelect, ToggleButtonItem, WO2Checkbox, WO2ToggleButtonGroup } from 'src/common';
import WO2Button from 'src/common/components/button/Button';
import DateRangePicker from 'src/common/components/DateRangePicker';
import { DatePickerNames, DatePickerTypes } from 'src/common/components/DateRangePickerPreDefinedDates';
import { useDebounce } from 'src/common/hooks';
import { ReportingGenerationClientCsvModal } from 'src/features/reporting/components/generation/ReportingGenerationClientCsvModal';
import {
  ClientAccountTypeForSearch,
  ClientFilterDateRange,
  ClientSearchResult,
  ClientStatusType,
  GetFilteredClientEntity,
  ScheduledEventInput,
  ServiceType,
} from 'src/features/reporting/store/types';
import { DateRange } from 'src/store';
import { Props } from '../../container';
import { EntityLookup } from '../../store/types';

interface ClientFilterStepProps extends Props {
  formikProps: FormikProps<ScheduledEventInput>;
}

const serviceTypeToggleButtons: ToggleButtonItem<string>[] = [
  { name: 'All', value: '' },
  { name: ServiceType.Mda.displayName, value: ServiceType.Mda.name },
  { name: ServiceType.Soa.displayName, value: ServiceType.Soa.name },
];

export const ClientFilterStep = (props: ClientFilterStepProps) => {
  const [searchText, setSearchText] = useState<Record<string, string>>({
    AfslLicensee: '',
    Practice: '',
    Adviser: '',
    Group: '',
    Client: '',
  });
  const [searchEntity, setSearchEntity] = useState<keyof typeof searchText | ''>('');

  const [uploadCsvOpened, setUploadCsvOpened] = useState<boolean>(false);
  const [inactiveOnly, setInactiveOnly] = useState<boolean>(
    (props.formikProps.values.clientSearchFilter?.statuses ?? []).some((status) => status !== ClientStatusType.Active.name)
  );
  const [exitDate, setExitDate] = useState<ClientFilterDateRange | undefined>(props.formikProps.values.clientSearchFilter?.exitDate ?? undefined);
  const [inceptionDate, setInceptionDate] = useState<ClientFilterDateRange | undefined>(
    props.formikProps.values.clientSearchFilter?.inceptionDate ?? undefined
  );
  const debouncedSearchText = useDebounce(searchText, 500);
  const debouncedSearchEntity = useDebounce(searchEntity, 500);

  const toPickerDateRange = (range?: ClientFilterDateRange): DateRange => ({
    dateFrom: range?.fromDate ?? '',
    dateTo: range?.toDate ?? '',
    dateRangeType: range?.dateRangeType ?? DatePickerTypes.DateRange,
  });

  const toFilterDateRange = (range: DateRange): ClientFilterDateRange => ({
    fromDate: range.dateFrom,
    toDate: range.dateTo,
    dateRangeType: range.dateRangeType,
  });

  const getLastMonthDateRange = (): DateRange => {
    const lastMonth = DateTime.now().minus({ months: 1 });
    return {
      dateFrom: lastMonth.startOf('month').toISODate(),
      dateTo: lastMonth.endOf('month').toISODate(),
      dateRangeType: DatePickerNames.LastMonth,
    };
  };

  const handleClientFilterChange = <T,>(field: string, value: T) => {
    props.formikProps.setFieldValue(`clientSearchFilter.${field}`, value);
  };

  const updateExitDate = (range: DateRange | undefined) => {
    const dateRange = range?.dateFrom && range?.dateTo ? toFilterDateRange(range) : undefined;
    setExitDate(dateRange);
    handleClientFilterChange('exitDate', dateRange);
  };

  const updateInceptionDate = (range: DateRange | undefined) => {
    const dateRange = range?.dateFrom && range?.dateTo ? toFilterDateRange(range) : undefined;
    setInceptionDate(dateRange);
    handleClientFilterChange('inceptionDate', dateRange);
  };

  const handleInactiveOnlyToggle = (checked: boolean) => {
    setInactiveOnly(checked);
    props.formikProps.setFieldValue('allowTerminatedClients', checked);
    if (checked) {
      const statuses = (props.formikProps.values.clientSearchFilter?.statuses ?? []).filter((status) => status !== ClientStatusType.Active.name);
      handleClientFilterChange('statuses', statuses);
    } else {
      handleClientFilterChange('statuses', [ClientStatusType.Active.name]);
      updateExitDate(undefined);
    }
  };

  const closeCsvUpload = () => setUploadCsvOpened(false);

  const handleAppendClientsFromCsv = (clients: ClientSearchResult[], allowTerminatedClients: boolean) => {
    const clientIds = clients.map((client) => client.clientId);

    props.rememberClients(clients.map((client) => ({ entityCoreId: client.clientId, name: client.clientName })));

    props.formikProps.setFieldValue('clientSearchFilter.clientIds', clientIds);
    props.formikProps.setFieldValue('allowTerminatedClients', allowTerminatedClients);
  };

  const uploadClientList = props.formikProps.values.clientSearchFilter?.clientIds ?? [];

  const resolveSelected = (lookup: EntityLookup, ids: number[]) =>
    ids.map((id) => lookup[id]).filter((e): e is GetFilteredClientEntity => e !== undefined);

  const { knownEntities } = props.scheduler;
  const selectedAfslsItems = useMemo(
    () => resolveSelected(knownEntities.afsls, props.formikProps.values.clientSearchFilter?.afslIds ?? []),
    [props.formikProps.values.clientSearchFilter?.afslIds, knownEntities.afsls]
  );
  const selectedPracticeItems = useMemo(
    () => resolveSelected(knownEntities.practices, props.formikProps.values.clientSearchFilter?.practiceIds ?? []),
    [props.formikProps.values.clientSearchFilter?.practiceIds, knownEntities.practices]
  );
  const selectedAdvisers = useMemo(
    () => resolveSelected(knownEntities.advisers, props.formikProps.values.clientSearchFilter?.adviserIds ?? []),
    [props.formikProps.values.clientSearchFilter?.adviserIds, knownEntities.advisers]
  );
  const selectedGroups = useMemo(
    () => resolveSelected(knownEntities.groups, props.formikProps.values.clientSearchFilter?.groupIds ?? []),
    [props.formikProps.values.clientSearchFilter?.groupIds, knownEntities.groups]
  );
  const selectedClients = useMemo(
    () => resolveSelected(knownEntities.clients, props.formikProps.values.clientSearchFilter?.clientIds ?? []),
    [props.formikProps.values.clientSearchFilter?.clientIds, knownEntities.clients]
  );
  const selectedModel = useMemo(
    () => props.bulk.models.find((model) => model.modelId === props.formikProps.values.clientSearchFilter?.modelId) ?? null,
    [props.bulk.models, props.formikProps.values.clientSearchFilter?.modelId]
  );

  const updateSearch = (entity: keyof typeof searchText, value: string, reason: string) => {
    if (reason !== 'reset') {
      setSearchText((prev) => ({ ...prev, [entity]: value }));
      setSearchEntity(entity);
    }
  };

  useEffect(() => {
    const serviceType = props.formikProps.values.clientSearchFilter?.serviceType;
    const groupSearchUnavailable = inactiveOnly || props.scheduler.allowStaticClients;
    const searchText = groupSearchUnavailable && debouncedSearchEntity === 'Group' ? '' : debouncedSearchText[debouncedSearchEntity];
    const clientStatus = inactiveOnly ? props.formikProps.values.clientSearchFilter?.statuses ?? [] : [ClientStatusType.Active.name];
    const hasExitDateRange = inactiveOnly && exitDate?.fromDate && exitDate?.toDate;
    const hasInceptionDateRange = inceptionDate?.fromDate && inceptionDate?.toDate;
    props.fetchFilteredReportClients({
      afslIds: props.formikProps.values.clientSearchFilter?.afslIds ?? [],
      practiceIds: props.formikProps.values.clientSearchFilter?.practiceIds ?? [],
      adviserIds: props.formikProps.values.clientSearchFilter?.adviserIds ?? [],
      accountTypes: props.formikProps.values.clientSearchFilter?.clientTypes ?? [],
      clientStatus: clientStatus,
      serviceTypes: serviceType ? [serviceType] : [],
      modelId: props.formikProps.values.clientSearchFilter?.modelId,
      searchText: searchText,
      searchEntity: searchText ? debouncedSearchEntity : undefined,
      exitDate: hasExitDateRange ? exitDate ?? undefined : undefined,
      inceptionDate: hasInceptionDateRange ? inceptionDate ?? undefined : undefined,
    });
  }, [
    props.formikProps.values.clientSearchFilter?.afslIds,
    props.formikProps.values.clientSearchFilter?.practiceIds,
    props.formikProps.values.clientSearchFilter?.adviserIds,
    props.formikProps.values.clientSearchFilter?.clientTypes,
    props.formikProps.values.clientSearchFilter?.statuses,
    props.formikProps.values.clientSearchFilter?.serviceType,
    props.formikProps.values.clientSearchFilter?.modelId,
    debouncedSearchText,
    debouncedSearchEntity,
    inactiveOnly,
    exitDate,
    inceptionDate,
    props.scheduler.allowStaticClients,
  ]);

  useEffect(() => {
    if (inactiveOnly || props.scheduler.allowStaticClients) {
      if (props.formikProps.values.clientSearchFilter?.groupIds?.length) {
        handleClientFilterChange('groupIds', []);
      }
      setSearchText((prev) => (prev.Group ? { ...prev, Group: '' } : prev));
      setSearchEntity((prev) => (prev === 'Group' ? '' : prev));
    }
  }, [inactiveOnly, props.scheduler.allowStaticClients, props.formikProps.values.clientSearchFilter?.groupIds]);

  useEffect(() => {
    props.fetchAfsls();
    if (!inactiveOnly && (props.formikProps.values.clientSearchFilter?.statuses ?? []).length === 0) {
      handleClientFilterChange('statuses', [ClientStatusType.Active.name]);
    }
  }, []);

  useEffect(() => {
    if (props.scheduler.savedParameter) {
      props.getReportParametersById({
        id: props.scheduler.savedParameter.id,
      });
    }
  }, [props.scheduler.savedParameter]);

  useEffect(() => {
    props.fetchModelList(props.formikProps.values.clientSearchFilter?.afslIds ?? []);
  }, [props.formikProps.values.clientSearchFilter?.afslIds]);

  useEffect(() => {
    const modelId = props.formikProps.values.clientSearchFilter?.modelId;
    if (modelId && props.bulk.models.length > 0 && !props.bulk.models.some((model) => model.modelId === modelId)) {
      handleClientFilterChange('modelId', undefined);
    }
  }, [props.bulk.models]);

  const serviceTypeToggle = (
    <WO2ToggleButtonGroup
      fullWidth
      buttons={serviceTypeToggleButtons}
      value={props.formikProps.values.clientSearchFilter?.serviceType ?? ''}
      onChangeHandler={(value) => handleClientFilterChange('serviceType', value)}
    />
  );

  return (
    <Grid container spacing={2} direction="column">
      {props.scheduler.allowStaticClients && uploadClientList.length > 0 && (
        <Grid item>
          <Alert severity="info">
            <Typography>{uploadClientList.length} clients included</Typography>
          </Alert>
        </Grid>
      )}

      <Grid item container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            id="afsl-multiselect"
            multiple
            fullWidth
            options={props.scheduler.afsls}
            loading={props.fetchFilteredReportClientsProgress.isLoading}
            disabled={selectedClients.length > 0 || selectedGroups.length > 0 || props.scheduler.allowStaticClients}
            value={selectedAfslsItems}
            getOptionLabel={(option) => option.name ?? ''}
            renderInput={(params) => <TextField {...params} margin="normal" label="AFSL" value={searchText.AfslLicensee} fullWidth />}
            inputValue={searchText.AfslLicensee}
            onInputChange={(_e, value, reason) => updateSearch('AfslLicensee', value, reason)}
            onChange={(_e, values) => {
              handleClientFilterChange(
                'afslIds',
                values.map((v) => v.entityCoreId)
              );
              setSearchText((prev) => ({ ...prev, AfslLicensee: '' }));
            }}
            isOptionEqualToValue={(option, value) => option.entityCoreId === value.entityCoreId}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            id="practice-multiselect"
            multiple
            fullWidth
            options={props.scheduler.practices}
            loading={props.fetchFilteredReportClientsProgress.isLoading}
            value={selectedPracticeItems}
            getOptionLabel={(option) => option.name ?? ''}
            renderInput={(params) => <TextField {...params} margin="normal" label="Practice" value={searchText.Practice} fullWidth />}
            inputValue={searchText.Practice}
            onInputChange={(_e, value, reason) => updateSearch('Practice', value, reason)}
            onChange={(_e, values) => {
              handleClientFilterChange(
                'practiceIds',
                values.map((v) => v.entityCoreId)
              );
              setSearchText((prev) => ({ ...prev, Practice: '' }));
            }}
            isOptionEqualToValue={(option, value) => option.entityCoreId === value.entityCoreId}
          />
        </Grid>
      </Grid>

      <Grid item container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            id="adviser-multiselect"
            multiple
            fullWidth
            options={props.scheduler.advisers}
            loading={props.fetchFilteredReportClientsProgress.isLoading}
            value={selectedAdvisers}
            getOptionLabel={(option) => option.name ?? ''}
            renderInput={(params) => <TextField {...params} margin="normal" label="Adviser" value={searchText.Adviser} fullWidth />}
            inputValue={searchText.Adviser}
            onInputChange={(_e, value, reason) => updateSearch('Adviser', value, reason)}
            onChange={(_e, values) => {
              handleClientFilterChange(
                'adviserIds',
                values.map((v) => v.entityCoreId)
              );
              setSearchText((prev) => ({ ...prev, Adviser: '' }));
            }}
            isOptionEqualToValue={(option, value) => option.entityCoreId === value.entityCoreId}
          />
        </Grid>

        {!inactiveOnly && (
          <Grid item xs={12} sm={6}>
            <Autocomplete
              id="group-multiselect"
              multiple
              fullWidth
              options={props.scheduler.groups}
              loading={props.fetchFilteredReportClientsProgress.isLoading}
              value={selectedGroups}
              disabled={props.scheduler.allowStaticClients}
              getOptionLabel={(option) => option.name ?? ''}
              renderInput={(params) => <TextField {...params} margin="normal" label="Group Search" value={searchText.Group} fullWidth />}
              inputValue={searchText.Group}
              onInputChange={(_e, value, reason) => updateSearch('Group', value, reason)}
              onChange={(_e, values) => {
                handleClientFilterChange(
                  'groupIds',
                  values.map((v) => v.entityCoreId)
                );
                setSearchText((prev) => ({ ...prev, Group: '' }));
              }}
              isOptionEqualToValue={(option, value) => option.entityCoreId === value.entityCoreId}
            />
          </Grid>
        )}
      </Grid>

      <Grid item container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            id="client-multiselect"
            multiple
            fullWidth
            options={props.scheduler.clients}
            loading={props.fetchFilteredReportClientsProgress.isLoading}
            value={selectedClients}
            disabled={props.scheduler.allowStaticClients}
            getOptionLabel={(option) => `${option.name ?? ''} (${option.entityCoreId ?? 0})`}
            renderInput={(params) => <TextField {...params} margin="normal" label="Client Search" value={searchText.Client} fullWidth />}
            inputValue={searchText.Client}
            onInputChange={(_e, value, reason) => updateSearch('Client', value, reason)}
            onChange={(_e, values) => {
              handleClientFilterChange(
                'clientIds',
                values.map((v) => v.entityCoreId)
              );
              setSearchText((prev) => ({ ...prev, Client: '' }));
            }}
            isOptionEqualToValue={(option, value) => option.entityCoreId === value.entityCoreId}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <EnumerationMultiSelect
            label="Client Type"
            type={ClientAccountTypeForSearch}
            value={props.formikProps.values.clientSearchFilter?.clientTypes ?? []}
            onChange={(values) => handleClientFilterChange('clientTypes', values)}
          />
        </Grid>
      </Grid>

      <Grid item container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6}>
          <Autocomplete
            id="model-select"
            fullWidth
            options={props.bulk.models}
            loading={props.fetchFilteredReportClientsProgress.isLoading}
            disabled={props.scheduler.allowStaticClients}
            value={selectedModel}
            getOptionLabel={(option) => (option.code ? `${option.name} (${option.code})` : option.name ?? '')}
            renderInput={(params) => <TextField {...params} margin="normal" label="Model" fullWidth />}
            onChange={(_e, value) => handleClientFilterChange('modelId', value?.modelId)}
            isOptionEqualToValue={(option, value) => option.modelId === value.modelId}
          />
        </Grid>
        <Grid item xs={12} sm={6} mt={1}>
          {serviceTypeToggle}
        </Grid>
      </Grid>

      <Grid item container alignItems="center" spacing={2} mt={1}>
        <Grid item xs={12} sm={6}>
          <DateRangePicker
            dateRange={toPickerDateRange(inceptionDate)}
            dateRangeLabel="Inception Date"
            setDateRange={updateInceptionDate}
            onRemove={() => updateInceptionDate(undefined)}
            emptyRangeDefault={getLastMonthDateRange()}
          />
        </Grid>
      </Grid>

      {inactiveOnly && (
        <Grid item container alignItems="center" spacing={2}>
          <Grid item xs={12} sm={6}>
            <EnumerationMultiSelect
              label="Status"
              type={ClientStatusType}
              hidden={[ClientStatusType.Active.name]}
              value={props.formikProps.values.clientSearchFilter?.statuses ?? []}
              onChange={(values) => handleClientFilterChange('statuses', values)}
            />
          </Grid>
          <Grid item xs={12} sm={6} mt={1}>
            <DateRangePicker
              dateRange={toPickerDateRange(exitDate)}
              dateRangeLabel="Exit Date"
              setDateRange={updateExitDate}
              onRemove={() => updateExitDate(undefined)}
              emptyRangeDefault={getLastMonthDateRange()}
            />
          </Grid>
        </Grid>
      )}

      <Grid item mt={2}>
        <Divider />
      </Grid>

      <Grid item container spacing={2} alignItems="center">
        <Grid item xs={12} sm="auto">
          <WO2Checkbox label="Search for inactive clients" checked={inactiveOnly} onChangeHandler={handleInactiveOnlyToggle} />
        </Grid>
        <Grid item xs={12} sm="auto">
          <WO2Checkbox
            label="Upload static client list"
            onChangeHandler={(value) => {
              props.setAllowStaticClients(value);
              props.formikProps.setFieldValue('clientSearchFilter.clientIds', []);
            }}
            checked={props.scheduler.allowStaticClients}
          />
        </Grid>
        {props.scheduler.allowStaticClients && (
          <Grid item xs={12} sm="auto">
            <WO2Button onClick={() => setUploadCsvOpened(true)} variant="contained">
              Import Client List
            </WO2Button>
          </Grid>
        )}
      </Grid>

      <ReportingGenerationClientCsvModal
        {...props}
        handleAppendClientsFromCsv={handleAppendClientsFromCsv}
        isOpen={uploadCsvOpened}
        handleClose={closeCsvUpload}
      />
    </Grid>
  );
};
